/**
 * Client-assignment QuestionnaireResponses in chart readers (ENG-3764 task 6.1).
 *
 * Assigning a form or assessment saves the client's QR up front with chart status `pending-client` (D1). Those QRs
 * carry the chart-document status extension but none of the sign-settings extensions, which only chart documents
 * have (`applyChartDocumentSignSettings`, the same discriminator the `portal-task-lifecycle` listener uses, D2). An
 * unanswered (or stopped) assignment QR is not a result: Measures results and trends, the session assessment views
 * and Forms & Files must not show it. A completed one is an ordinary form or assessment response, not a chart
 * document.
 */
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  CHART_DOCUMENT_CLIENT_SIGN_REQUIRED_EXT,
  CHART_DOCUMENT_PROVIDER_SIGN_REQUIRED_EXT,
  CHART_DOCUMENT_STATUS_EXT,
} from '@unify/fhir-model/constants';
import { getAssignmentTemplateCategory } from '@unify/fhir-model/resources/client-assignment';

const SIGN_SETTINGS_EXTS = new Set([CHART_DOCUMENT_CLIENT_SIGN_REQUIRED_EXT, CHART_DOCUMENT_PROVIDER_SIGN_REQUIRED_EXT]);

/** A forms or measures assignment QR (chart status, a forms/measures tag, no chart-document sign settings). */
export function isClientAssignmentResponse(qr: QuestionnaireResponse): boolean {
  const extensions = qr.extension ?? [];
  if (!extensions.some((e) => e.url === CHART_DOCUMENT_STATUS_EXT)) return false;
  if (extensions.some((e) => SIGN_SETTINGS_EXTS.has(e.url))) return false;
  return getAssignmentTemplateCategory(qr) !== undefined;
}

/** An assignment QR the client has not answered: still pending, or stopped/rejected by a cancel or the listener. */
export function isUnansweredClientAssignmentResponse(qr: QuestionnaireResponse): boolean {
  return isClientAssignmentResponse(qr) && qr.status !== 'completed' && qr.status !== 'amended';
}
